import { Device, DevicesType, SelectedDeviceId } from '../types';
import { FC, SyntheticEvent, useEffect, useRef, useState } from 'react';
import { Box, LinearProgress, Link, Tooltip, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import OverflowTooltip from '@shared/components/ui/OverflowTooltip';
import BookingDeviceFullInfoDialog from './BookingDeviceFullInfoDialog';
import { getBookingsReset, getFullDeviceInfoRequest } from '../store';
import { useDispatch } from 'react-redux';
import { FavoriteObjectEntity } from '@features/favorites/types';
import { MAP_DEVICE_TYPE_TO_LABEL } from '@pages/booking/constants';
import { FavoriteButton } from '@features/favorites';

interface BookingDeviceItemProps {
    device: Device;
    selectedDeviceId: SelectedDeviceId;
    setSelectedDeviceId: (newSelectedDeviceId: SelectedDeviceId) => void;
}

const BookingDeviceItem: FC<BookingDeviceItemProps> = ({ device, selectedDeviceId, setSelectedDeviceId }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const dispatch = useDispatch();
    const [deviceFullInfoDialogOpen, setDeviceFullInfoDialogOpen] = useState(false);
    const deviceWorkload = Number(device.workload);
    const itemRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (selectedDeviceId === device.pk && itemRef.current) {
            itemRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [selectedDeviceId, device.pk]);

    const handleOpenDeviceFullInfoDialogOpen = (e: SyntheticEvent) => {
        e.stopPropagation();
        setDeviceFullInfoDialogOpen(true);
        dispatch(getFullDeviceInfoRequest(device.pk));
    };

    const handleCloseDeviceFullInfoDialogOpen = (e: SyntheticEvent) => {
        e.stopPropagation();
        setDeviceFullInfoDialogOpen(false);
    };

    const handleSelectDevice = () => {
        if (selectedDeviceId !== device.pk) {
            dispatch(getBookingsReset());
            setSelectedDeviceId(device.pk);
        }
    };

    return (
        <Box
            sx={{
                height: 80,
                padding: '8px 16px',
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid var(--border-color)',
                maxWidth: '100%',
                cursor: 'pointer',
                // @ts-ignore
                background: selectedDeviceId === device.pk ? theme.palette.primary.selected : '',
            }}
            ref={itemRef}
            onClick={handleSelectDevice}
        >
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    gap: 0.5,
                    maxWidth: '85%',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '0.75rem',
                        color: 'var(--text-secondary)',
                    }}
                >
                    <Box>{device.type === DevicesType.DUT ? t('name') : MAP_DEVICE_TYPE_TO_LABEL[device.type]}</Box>
                    <Box>{t('location')}</Box>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        gap: 0.25,
                        justifyContent: 'space-between',
                        fontSize: '1rem',
                        maxWidth: '100%',
                    }}
                >
                    <OverflowTooltip
                        sx={{
                            minWidth: 0,
                            flex: '0 1 auto',
                            maxWidth: '200px',
                            cursor: 'pointer',
                            fontSize: 'inherit',
                        }}
                        title={device.name}
                        onClick={handleOpenDeviceFullInfoDialogOpen}
                    >
                        <Link>{device.name}</Link>
                    </OverflowTooltip>
                    <Box sx={{ minWidth: 0, flex: 1, textAlign: 'right' }}>
                        <OverflowTooltip sx={{ fontSize: 'inherit' }} title={device.office}>
                            {device.office}
                        </OverflowTooltip>
                    </Box>
                </Box>
                <Tooltip
                    slotProps={{
                        tooltip: {
                            sx: {
                                fontSize: '0.85rem',
                            },
                        },
                    }}
                    title={`${deviceWorkload.toFixed(1)}% ${t('workload')}`}
                >
                    <LinearProgress
                        sx={{ height: 2 }}
                        variant="determinate"
                        color={deviceWorkload >= 75 ? 'error' : deviceWorkload >= 25 ? 'warning' : 'success'}
                        value={deviceWorkload}
                    />
                </Tooltip>
            </Box>
            <FavoriteButton objectEntity={FavoriteObjectEntity.BOOKING_DEVICES} objectId={device.pk} />
            <BookingDeviceFullInfoDialog
                open={deviceFullInfoDialogOpen}
                onClose={handleCloseDeviceFullInfoDialogOpen}
            />
        </Box>
    );
};

export default BookingDeviceItem;
